import * as Yup from 'yup';

import {
  ILoanRequestRuleResponse,
  WarrantyDocumentType,
} from '../../../../../../../services/hookService/getLoanRequestRule/types';

const phoneRegExp = /^09\d{9}$/;
const sayadiIdRegExp = /^\d{16}$/;

const createPaperChequeValidation = (
  chequeKey: 'first' | 'second' | 'third',
  chequeTitle: string,
  isRequired: boolean,
) => {
  if (!isRequired) {
    return {
      [`${chequeKey}_check_image`]: Yup.mixed().nullable().notRequired(),

      [`${chequeKey}_sayadi_cheque_id`]: Yup.string().nullable().notRequired(),
    };
  }

  return {
    [`${chequeKey}_check_image`]: Yup.mixed().when(
      ['checkType'],
      (values: any, schema: any) => {
        return schema;
      },
    ),

    [`${chequeKey}_sayadi_cheque_id`]: Yup.string().when(
      'checkType',
      (checkType: any, schema: any) => {
        const type = Array.isArray(checkType) ? checkType[0] : checkType;

        if (type !== 'paper') {
          return schema.notRequired().nullable();
        }

        return schema
          .required(`شناسه صیاد ${chequeTitle} الزامی است`)
          .matches(sayadiIdRegExp, 'شناسه صیاد باید دقیقاً ۱۶ رقم باشد')
          .length(16, 'شناسه صیاد باید ۱۶ رقم باشد');
      },
    ),
  };
};

const createDigitalChequeValidation = (
  chequeKey: 'first' | 'second' | 'third',
  chequeTitle: string,
  isRequired: boolean,
) => {
  if (!isRequired) {
    return {
      [`${chequeKey}_digital_cheque_id`]: Yup.string().nullable().notRequired(),
    };
  }

  return {
    [`${chequeKey}_digital_cheque_id`]: Yup.string().when(
      'checkType',
      (checkType: any, schema: any) => {
        const type = Array.isArray(checkType) ? checkType[0] : checkType;

        if (type !== 'digital') {
          return schema.notRequired().nullable();
        }

        return schema
          .required(`شماره ${chequeTitle} الزامی است`)
          .matches(/^\d+$/, 'فقط عدد مجاز است');
      },
    ),
  };
};

const createPersonValidationSchema = (
  userType: 'SELF' | 'GUARANTOR',
  shouldAddExtraDocs: boolean,
  rules?: ILoanRequestRuleResponse,
) => {
  const isGuarantor = userType === 'GUARANTOR';

  const firstChequeRequired = isGuarantor
    ? (rules?.first_guarantor_cheque_price ?? 0) > 0
    : (rules?.owner_first_cheque_price ?? 0) > 0;

  const secondChequeRequired = isGuarantor
    ? false
    : (rules?.owner_second_cheque_price ?? 0) > 0;

  const thirdChequeRequired = isGuarantor
    ? false
    : (rules?.owner_third_cheque_price ?? 0) > 0;

  return Yup.object().shape({
    name: Yup.string().when([], {
      is: () => isGuarantor,
      then: schema =>
        schema
          .required('نام و نام خانوادگی الزامی است')
          .min(2, 'نام باید حداقل ۲ کاراکتر باشد')
          .matches(/^[\u0600-\u06FF\s]+$/, 'نام باید فقط شامل حروف فارسی باشد'),
      otherwise: schema => schema.notRequired(),
    }),

    national_code: Yup.string().when([], {
      is: () => isGuarantor,
      then: schema =>
        schema
          .required('کد ملی الزامی است')
          .length(10, 'کد ملی باید دقیقاً ۱۰ رقم باشد')
          .matches(/^\d{10}$/, 'کد ملی باید فقط شامل اعداد باشد'),
      otherwise: schema => schema.notRequired(),
    }),

    cellphone_number: Yup.string().when([], {
      is: () => isGuarantor,
      then: schema =>
        schema
          .required('شماره موبایل الزامی است')
          .matches(
            phoneRegExp,
            'شماره موبایل باید با ۰۹ شروع شده و ۱۱ رقم باشد',
          )
          .length(11, 'شماره موبایل باید ۱۱ رقم باشد')
          .test('phone-format', 'شماره موبایل باید معتبر باشد', value => {
            if (!value) return false;
            return /^09\d{9}$/.test(value);
          }),
      otherwise: schema => schema.notRequired(),
    }),

    checkType: Yup.string()
      .required('نوع چک الزامی است')
      .oneOf(['paper', 'digital'], 'نوع چک نامعتبر است'),

    // first cheque
    ...createPaperChequeValidation('first', 'چک اول', firstChequeRequired),

    ...createDigitalChequeValidation('first', 'چک اول', firstChequeRequired),

    // second cheque — self only
    ...(isGuarantor
      ? {
          second_check_image: Yup.mixed().notRequired().nullable(),
          second_sayadi_cheque_id: Yup.string().notRequired().nullable(),
          second_digital_cheque_id: Yup.string().notRequired().nullable(),
        }
      : {
          ...createPaperChequeValidation(
            'second',
            'چک دوم',
            secondChequeRequired,
          ),

          ...createDigitalChequeValidation(
            'second',
            'چک دوم',
            secondChequeRequired,
          ),
        }),

    // third cheque — self only
    ...(isGuarantor
      ? {
          third_check_image: Yup.mixed().notRequired().nullable(),
          third_sayadi_cheque_id: Yup.string().notRequired().nullable(),
          third_digital_cheque_id: Yup.string().notRequired().nullable(),
        }
      : {
          ...createPaperChequeValidation(
            'third',
            'چک سوم',
            thirdChequeRequired,
          ),

          ...createDigitalChequeValidation(
            'third',
            'چک سوم',
            thirdChequeRequired,
          ),
        }),

    job_type: Yup.mixed()
      .when([], {
        is: () => shouldAddExtraDocs,
        then: schema =>
          schema
            .required('نوع شغل الزامی است')
            .test('is-valid-job-type', 'نوع شغل نامعتبر است', value => {
              if (value === undefined || value === null || value === '')
                return false;
              return [1, 2, 3].includes(Number(value));
            }),
        otherwise: schema => schema.notRequired(),
      })
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === undefined ||
          originalValue === null
        ) {
          return undefined;
        }

        const num = Number(originalValue);

        return isNaN(num) ? originalValue : num;
      }),

    is_check_registered: Yup.boolean(),
    _allJobImagesUploaded: Yup.boolean(),

    loan_request: Yup.string().required(),
    loan_rule: Yup.string().required(),
  });
};

const createGlobalValidationSchema = () =>
  Yup.object().shape({
    parcel_tracking_code: Yup.string().when(
      'check_delivery_method',
      (checkDeliveryMethod: any, schema: any) => {
        const method = Array.isArray(checkDeliveryMethod)
          ? checkDeliveryMethod[0]
          : checkDeliveryMethod;

        if (method !== 'sent') {
          return schema.nullable().notRequired();
        }

        return schema
          .required('کد رهگیری الزامی است')
          .matches(/^\d+$/, 'کد رهگیری باید فقط شامل عدد باشد');
      },
    ),

    check_delivery_method: Yup.string().oneOf(['sent', 'delivered', '']),

    address: Yup.string()
      .min(5, 'آدرس متقاضی اعتبار باید حداقل ۵ کاراکتر باشد')
      .required('آدرس متقاضی اعتبار الزامی است'),

    zip_code: Yup.string()
      .length(10, 'کد پستی باید دقیقاً ۱۰ رقم باشد')
      .required('کد پستی متقاضی اعتبار الزامی است'),
  });

export const createBankDocsValidationSchema = (
  warrantyType: number | undefined,
  shouldAddExtraDocs: boolean,
  rules?: ILoanRequestRuleResponse,
) => {
  const schemaShape: Record<string, Yup.ObjectSchema<any>> = {};

  schemaShape.global = createGlobalValidationSchema();

  const createWithRules = (type: 'SELF' | 'GUARANTOR') =>
    createPersonValidationSchema(type, shouldAddExtraDocs, rules).concat(
      Yup.object().shape({}),
    );

  if (
    warrantyType === WarrantyDocumentType.OWN_CHECK ||
    warrantyType === WarrantyDocumentType.OWN_CHECK_SINGLE_GUARANTOR ||
    warrantyType === WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS
  ) {
    schemaShape.self = createWithRules('SELF');
  }

  if (
    warrantyType === WarrantyDocumentType.ONE_GUARANTOR ||
    warrantyType === WarrantyDocumentType.TWO_GUARANTORS ||
    warrantyType === WarrantyDocumentType.OWN_CHECK_SINGLE_GUARANTOR ||
    warrantyType === WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS
  ) {
    schemaShape['Guarantor-0'] = createWithRules('GUARANTOR');
  }

  if (
    warrantyType === WarrantyDocumentType.TWO_GUARANTORS ||
    warrantyType === WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS
  ) {
    schemaShape['Guarantor-1'] = createWithRules('GUARANTOR');
  }

  return Yup.object().shape(schemaShape);
};
